import { reflect } from '@effector/reflect';
import { Button, PasswordInput, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loginMutation } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, USERNAME_MIN_LENGTH } from '../../config';
import { loginPageModel } from '../../model';
import cls from './login-page-form.module.scss';

const UsernameField = reflect({
	view: TextInput,
	bind: {
		'data-test-id': 'username-input',
		value: loginPageModel.usernameField.$value,
		onChange: (e) => loginPageModel.usernameField.change(e.target.value),
		placeholder: translated('login-page.username'),
		errorProps: { 'data-test-id': 'username-input-error' },
		error: combine(
			[loginPageModel.usernameField.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('login-page.username'),
					minLength: USERNAME_MIN_LENGTH,
				}),
		),
	},
});

const PasswordField = reflect({
	view: PasswordInput,
	bind: {
		'data-test-id': 'password-input',
		value: loginPageModel.passwordField.$value,
		onChange: (e) => loginPageModel.passwordField.change(e.target.value),
		visibilityToggleIcon: ({ reveal }) => (reveal ? <EyeOff /> : <Eye />),
		placeholder: translated('login-page.password'),
		errorProps: { 'data-test-id': 'password-input-error' },
		error: combine(
			[loginPageModel.passwordField.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('login-page.password'),
					minLength: PASSWORD_MIN_LENGTH,
					maxLength: PASSWORD_MAX_LENGTH,
				}),
		),
	},
});

export const LoginPageForm = () => {
	const { t } = useTranslation();

	const [loading, submit] = useUnit([loginMutation.$pending, loginPageModel.form.submit]);

	return (
		<form
			data-test-id='login-page-form'
			className={cls.loginPageForm}
			onSubmit={(e) => {
				e.preventDefault();
				submit();
			}}
		>
			<h1 className={cls.loginPageFormTitle}>{t('login-page.login')}</h1>
			<UsernameField />
			<PasswordField />
			<Button
				fullWidth
				data-test-id='login-button'
				className={cls.loginPageFormLogin}
				type='submit'
				loading={loading}
			>
				{t('login-page.login')}
			</Button>
		</form>
	);
};
